// src/components/home/LatestArticles.jsx
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import { db } from "../../config/firebase";

const PAGE_SIZE = 10;

const LatestArticles = () => {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  /** 🔧 スレッドデータ加工処理 */
  const processThreads = useCallback(async (snapshot) => {
    return Promise.all(
      snapshot.docs.map(async (doc) => {
        const data = doc.data();
        let firstNodeId = data.firstNodeId || null;
        let currentNodeContent = "";

        // firstNodeId が未設定なら nodes コレクションから最初の node を取得
        if (!firstNodeId) {
          const nodesQuery = query(
            collection(db, "stories", doc.id, "nodes"),
            orderBy("createdAt", "asc"),
            limit(1)
          );
          const nodesSnapshot = await getDocs(nodesQuery);
          if (!nodesSnapshot.empty) {
            firstNodeId = nodesSnapshot.docs[0].id;
            currentNodeContent = nodesSnapshot.docs[0].data().content || "";
          }
        } else {
          // firstNodeId がある場合は content を取得
          const allNodes = await getDocs(
            collection(db, "stories", doc.id, "nodes")
          );
          const firstNodeDoc = allNodes.docs.find(
            (n) => n.id === firstNodeId
          );
          if (firstNodeDoc) {
            currentNodeContent = firstNodeDoc.data().content || "";
          }
        }

        return {
          id: doc.id,
          title: data.title,
          category: data.category || "未分類",
          firstNodeId,
          currentNodeContent,
          createdAt: data.createdAt,
        };
      })
    );
  }, []);

  /** 🔥 初回 10 件取得 */
  const fetchInitial = useCallback(async () => {
    setLoading(true);

    try {
      const q = query(
        collection(db, "stories"),
        orderBy("createdAt", "desc"),
        limit(PAGE_SIZE)
      );

      const snap = await getDocs(q);
      if (snap.empty) {
        setThreads([]);
        setHasMore(false);
        setLoading(false);
        return;
      }

      const fetched = await processThreads(snap);
      setThreads(fetched);
      setLastVisible(snap.docs[snap.docs.length - 1]);
      if (snap.docs.length < PAGE_SIZE) setHasMore(false);
    } catch (err) {
      console.error("最新スレッド初回取得失敗:", err);
    }

    setLoading(false);
  }, [processThreads]);

  /** 🔄 初回実行 */
  useEffect(() => {
    fetchInitial();
  }, [fetchInitial]);

  /** ➕ もっと見る（追加読み込み） */
  const loadMore = async () => {
    if (!lastVisible) return;

    setLoading(true);

    try {
      const q = query(
        collection(db, "stories"),
        orderBy("createdAt", "desc"),
        startAfter(lastVisible),
        limit(PAGE_SIZE)
      );

      const snap = await getDocs(q);
      if (snap.empty) {
        setHasMore(false);
        setLoading(false);
        return;
      }

      const fetched = await processThreads(snap);
      setThreads((prev) => [...prev, ...fetched]);
      setLastVisible(snap.docs[snap.docs.length - 1]);

      if (snap.docs.length < PAGE_SIZE) setHasMore(false);
    } catch (err) {
      console.error("追加読み込み失敗:", err);
    }

    setLoading(false);
  };

  if (threads.length === 0 && loading) return <p>読み込み中...</p>;

  return (
    <div>
      <h2>最新記事一覧</h2>

      <ul>
        {threads.map((thread) => (
          <li key={thread.id} style={{ marginBottom: "10px" }}>
            <Link
              to={`/chat/${thread.firstNodeId}`}
              state={{
                storyId: thread.id,
                nodeId: thread.firstNodeId,
              }}
            >
              {thread.title}
            </Link>

            <span
              style={{
                display: "block",
                marginBottom: "2px",
                fontSize: "14px",
                color: "#555",
              }}
            >
              {thread.currentNodeContent?.slice(0, 50)}
            </span>

            <span
              style={{
                display: "block",
                fontSize: "12px",
                color: "#999",
              }}
            >
              {thread.createdAt?.toDate().toLocaleDateString()}
            </span>
          </li>
        ))}
      </ul>

      {/* 🔽 もっと見るボタン */}
      {hasMore && (
        <button
          onClick={loadMore}
          disabled={loading}
          style={{
            marginTop: "10px",
            padding: "8px 14px",
            cursor: "pointer",
            borderRadius: "6px",
            border: "1px solid #ccc",
          }}
        >
          {loading ? "読み込み中..." : "もっと見る"}
        </button>
      )}
    </div>
  );
};

export default LatestArticles;
